<script lang="ts" setup>
const props = defineProps<{
  imageUrls: string[]
  autoplay?: boolean
}>()

const targetIndex = ref(0)
const actualIndex = ref(0)
const overrideOffset = ref<number | null>(null)
const animationDuration = ref(1000)
let mounted = false

const movingDiv = ref<HTMLDivElement | null>(null)

function render() {
  if (!mounted || !movingDiv.value) {
    return
  }

  const computedStyle = getComputedStyle(movingDiv.value)
  const transform = computedStyle.transform
  if (transform === 'none') {
    actualIndex.value = 0
  } else {
    const xOffset = Number(transform.slice('matrix('.length, -1).split(', ')[4])
    actualIndex.value = -xOffset / Number(computedStyle.width.slice(0, -2))
  }

  requestAnimationFrame(render)
}

let intervalId = -1
function startAutoplay() {
  if (!props.autoplay) {
    return
  }

  stopAutoplay()

  intervalId = setInterval(() => {
    if (props.imageUrls.length <= 1 || document.hidden) {
      return
    }

    targetIndex.value = targetIndex.value + 1
    animationDuration.value = 1000
  }, 5000) as unknown as number
}

function stopAutoplay() {
  clearInterval(intervalId)
}

onMounted(() => {
  mounted = true
  render()
  startAutoplay()
})

onUnmounted(() => {
  mounted = false
})

function onPointerDown(event: PointerEvent) {
  if (!movingDiv.value || props.imageUrls.length <= 1) {
    return
  }

  event.preventDefault()
  stopAutoplay()

  const computedStyle = getComputedStyle(movingDiv.value)
  const xOffset = Number(computedStyle.transform.slice('matrix('.length, -1).split(', ')[4])
  overrideOffset.value = xOffset

  const velocityWindow = 100
  const movements: { time: number; x: number }[] = []
  let velocity = 0

  function onPointerMove(moveEvent: PointerEvent) {
    overrideOffset.value! += moveEvent.movementX

    const now = performance.now()
    movements.push({
      time: now,
      x: moveEvent.movementX,
    })

    // Remove events older than velocityWindow
    while (movements.length > 0 && now - movements[0].time > velocityWindow) {
      movements.shift()
    }

    if (movements.length >= 2) {
      const totalTime = (movements.at(-1)!.time - movements[0].time) / 1000
      const totalDistance = movements.reduce((sum, m) => sum + m.x, 0)
      velocity = totalDistance / totalTime
    }
  }
  addEventListener('pointermove', onPointerMove)

  addEventListener(
    'pointerup',
    () => {
      const hoveredIndex = Math.round(
        -(overrideOffset.value! + velocity / 10) / Number(computedStyle.width.slice(0, -2)),
      )
      targetIndex.value = hoveredIndex

      removeEventListener('pointermove', onPointerMove)
      overrideOffset.value = null

      // Make sure the animation is faster than usual when we have less than one width to travel
      animationDuration.value = 1000 * Math.min(Math.abs(hoveredIndex - actualIndex.value), 1)

      startAutoplay()
    },
    { once: true },
  )
}

function cycle(n: number) {
  targetIndex.value += n
  animationDuration.value = 1000
  startAutoplay()
}
</script>

<template>
  <div
    class="relative aspect-[3/2] overflow-hidden rounded-md select-none"
    :class="{
      'touch-none': props.imageUrls.length > 1,
    }"
    @pointerdown="onPointerDown"
  >
    <div
      ref="movingDiv"
      class="size-full relative ease-[cubic-bezier(0.22,_1,_0.36,_1)]"
      :class="{
        'transition-transform': overrideOffset === null,
      }"
      :style="{
        transform:
          overrideOffset !== null
            ? `translateX(${overrideOffset}px)`
            : `translateX(${-targetIndex * 100}%)`,
        transitionDuration: overrideOffset === null ? `${animationDuration}ms` : undefined,
      }"
    >
      <img
        v-for="(url, i) in imageUrls"
        :key="i"
        :src="url"
        class="absolute top-0 size-full object-cover"
        :style="{
          // Position each image such that it closest to the actual index, while still being in the correct order
          left: `${(imageUrls.length * Math.round((actualIndex - i) / imageUrls.length) + i) * 100}%`,
        }"
      />
    </div>

    <button
      v-if="imageUrls.length > 1"
      type="button"
      class="absolute left-2 top-1/2 -translate-y-1/2 opacity-50 hover:opacity-100 hover:scale-110 transition-all duration-150"
      @pointerdown.stop="cycle(-1)"
    >
      <IHeroiconsChevronLeft class="size-8 text-white" />
    </button>

    <button
      v-if="imageUrls.length > 1"
      type="button"
      class="absolute right-2 top-1/2 -translate-y-1/2 opacity-50 hover:opacity-100 hover:scale-110 transition-all duration-150"
      @pointerdown.stop="cycle(1)"
    >
      <IHeroiconsChevronRight class="size-8 text-white" />
    </button>
  </div>
</template>
